import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { GameState, Player, Team, Army, SoldierType, Soldier, SiegeEquipment, UnitStats, DEFAULT_UNIT_STATS } from './types';
import { gameService } from './services/gameService';

interface GameContextType {
  gameState: GameState;
  loading: boolean;
  error: string | null;
  addPlayer: (name: string, team: Team, userId?: string) => Promise<void>;
  removePlayer: (playerId: string) => Promise<void>;
  updatePlayerRaidPoints: (playerId: string, points: number) => Promise<void>;
  switchPlayerTeam: (playerId: string) => Promise<void>;
  addSoldierToArmy: (playerId: string, soldierType: SoldierType) => Promise<boolean>;
  removeSoldierFromArmy: (playerId: string, soldierType: SoldierType, soldierId: string) => Promise<void>;
  resetPlayerArmy: (playerId: string) => Promise<void>;
  manSiegeEquipment: (playerId: string, siegeId: string, soldierId: string) => void;
  unmanSiegeEquipment: (playerId: string, siegeId: string, soldierId: string) => void;
  getPlayer: (playerId: string) => Player | undefined;
  updateUnitStats: (soldierType: SoldierType, stats: Partial<UnitStats>) => Promise<void>;
  linkUserIdToPlayer: (playerId: string, userId: string) => Promise<void>;
  getOrCreatePlayerForUser: (userId: string, username: string, team: Team) => Promise<Player>;
  refreshGameState: () => Promise<void>;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

export const GameProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [gameState, setGameState] = useState<GameState>({
    players: [],
    unitStats: DEFAULT_UNIT_STATS
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Load initial game state from backend
  const refreshGameState = async () => {
    try {
      setLoading(true);
      setError(null);
      const state = await gameService.getGameState();
      setGameState(state);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load game state';
      setError(errorMessage);
      console.error('Failed to load game state:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load game state on mount
  useEffect(() => {
    refreshGameState();
  }, []);

  const addPlayer = async (name: string, team: Team, userId?: string) => {
    try {
      setError(null);
      const newPlayer = await gameService.createPlayer(name, team, userId);
      setGameState(prev => ({
        ...prev,
        players: [...prev.players, newPlayer]
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add player';
      setError(errorMessage);
      console.error('Failed to add player:', err);
      throw err;
    }
  };

  const linkUserIdToPlayer = async (playerId: string, userId: string) => {
    try {
      setError(null);
      const player = gameState.players.find(p => p.id === playerId);
      if (!player) {
        throw new Error('Player not found');
      }

      const updatedPlayer = await gameService.updatePlayer(playerId, {
        userId
      });

      setGameState(prev => ({
        ...prev,
        players: prev.players.map(p => p.id === playerId ? updatedPlayer : p)
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to link user';
      setError(errorMessage);
      console.error('Failed to link user to player:', err);
      throw err;
    }
  };

  const getOrCreatePlayerForUser = async (userId: string, username: string, team: Team): Promise<Player> => {
    try {
      setError(null);

      // Check if player exists in current state
      const existingPlayer = gameState.players.find(p => p.userId === userId);
      if (existingPlayer) {
        return existingPlayer;
      }

      // Check backend for existing player
      const backendPlayer = await gameService.getPlayerByUserId(userId);
      if (backendPlayer) {
        // Update local state
        setGameState(prev => ({
          ...prev,
          players: [...prev.players, backendPlayer]
        }));
        return backendPlayer;
      }

      // Create new player
      const newPlayer = await gameService.createPlayer(username, team, userId);
      setGameState(prev => ({
        ...prev,
        players: [...prev.players, newPlayer]
      }));
      return newPlayer;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get or create player';
      setError(errorMessage);
      console.error('Failed to get or create player:', err);
      throw err;
    }
  };

  const removePlayer = async (playerId: string) => {
    try {
      setError(null);
      await gameService.deletePlayer(playerId);
      setGameState(prev => ({
        ...prev,
        players: prev.players.filter(p => p.id !== playerId)
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove player';
      setError(errorMessage);
      console.error('Failed to remove player:', err);
      throw err;
    }
  };

  const updatePlayerRaidPoints = async (playerId: string, points: number) => {
    try {
      setError(null);
      const updatedPlayer = await gameService.updatePlayer(playerId, {
        raidPoints: points
      });
      setGameState(prev => ({
        ...prev,
        players: prev.players.map(p => p.id === playerId ? updatedPlayer : p)
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update raid points';
      setError(errorMessage);
      console.error('Failed to update raid points:', err);
      throw err;
    }
  };

  const switchPlayerTeam = async (playerId: string) => {
    try {
      setError(null);
      const player = gameState.players.find(p => p.id === playerId);
      if (!player) {
        throw new Error('Player not found');
      }

      const newTeam: Team = player.team === 'raiders' ? 'defenders' : 'raiders';
      const updatedPlayer = await gameService.updatePlayer(playerId, {
        team: newTeam
      });
      setGameState(prev => ({
        ...prev,
        players: prev.players.map(p => p.id === playerId ? updatedPlayer : p)
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to switch team';
      setError(errorMessage);
      console.error('Failed to switch team:', err);
      throw err;
    }
  };

  const addSoldierToArmy = async (playerId: string, soldierType: SoldierType): Promise<boolean> => {
    try {
      setError(null);
      const player = gameState.players.find(p => p.id === playerId);
      if (!player) return false;

      const cost = gameState.unitStats[soldierType].cost;
      const remainingPoints = player.raidPoints - player.raidPointsSpent;
      if (remainingPoints < cost) return false;

      // Create new soldier locally first for immediate feedback
      const newSoldier: Soldier = {
        id: Date.now().toString() + Math.random(),
        type: soldierType
      };

      const updatedArmy = { ...player.army };

      if (soldierType === 'Knight') {
        updatedArmy.knights = [...updatedArmy.knights, newSoldier];
      } else if (soldierType === 'Archer') {
        updatedArmy.archers = [...updatedArmy.archers, newSoldier];
      } else if (soldierType === 'Crossbow') {
        updatedArmy.crossbows = [...updatedArmy.crossbows, newSoldier];
      } else if (soldierType === 'Healer') {
        updatedArmy.healers = [...updatedArmy.healers, newSoldier];
      } else if (soldierType === 'Siege Equipment') {
        const siegeEquipment: SiegeEquipment = {
          ...newSoldier,
          type: 'Siege Equipment',
          manning: []
        };
        updatedArmy.siegeEquipment = [...updatedArmy.siegeEquipment, siegeEquipment];
      }

      // Update backend - increment spent points instead of decrementing total points
      const updatedPlayer = await gameService.updatePlayer(playerId, {
        raidPointsSpent: player.raidPointsSpent + cost,
        army: updatedArmy
      });

      // Update local state
      setGameState(prev => ({
        ...prev,
        players: prev.players.map(p => p.id === playerId ? updatedPlayer : p)
      }));

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add soldier';
      setError(errorMessage);
      console.error('Failed to add soldier to army:', err);
      return false;
    }
  };

  const removeSoldierFromArmy = async (playerId: string, soldierType: SoldierType, soldierId: string) => {
    try {
      setError(null);
      const player = gameState.players.find(p => p.id === playerId);
      if (!player) return;

      const updatedArmy = { ...player.army };
      const cost = gameState.unitStats[soldierType].cost;

      if (soldierType === 'Knight') {
        updatedArmy.knights = updatedArmy.knights.filter(s => s.id !== soldierId);
      } else if (soldierType === 'Archer') {
        updatedArmy.archers = updatedArmy.archers.filter(s => s.id !== soldierId);
      } else if (soldierType === 'Crossbow') {
        updatedArmy.crossbows = updatedArmy.crossbows.filter(s => s.id !== soldierId);
      } else if (soldierType === 'Healer') {
        updatedArmy.healers = updatedArmy.healers.filter(s => s.id !== soldierId);
      } else if (soldierType === 'Siege Equipment') {
        updatedArmy.siegeEquipment = updatedArmy.siegeEquipment.filter(s => s.id !== soldierId);
      }

      // Update backend - decrease spent points when removing a unit
      const updatedPlayer = await gameService.updatePlayer(playerId, {
        raidPointsSpent: Math.max(0, player.raidPointsSpent - cost),
        army: updatedArmy
      });

      // Update local state
      setGameState(prev => ({
        ...prev,
        players: prev.players.map(p => p.id === playerId ? updatedPlayer : p)
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove soldier';
      setError(errorMessage);
      console.error('Failed to remove soldier from army:', err);
      throw err;
    }
  };

  const resetPlayerArmy = async (playerId: string) => {
    try {
      setError(null);
      const player = gameState.players.find(p => p.id === playerId);
      if (!player) {
        throw new Error('Player not found');
      }

      // Call backend to reset army (keeps King, removes everything else, resets spent points)
      const updatedPlayer = await gameService.resetPlayerArmy(playerId);

      // Update local state
      setGameState(prev => ({
        ...prev,
        players: prev.players.map(p => p.id === playerId ? updatedPlayer : p)
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset army';
      setError(errorMessage);
      console.error('Failed to reset player army:', err);
      throw err;
    }
  };

  // These operations are local-only (don't need backend sync for UI state like siege manning)
  const manSiegeEquipment = (playerId: string, siegeId: string, soldierId: string) => {
    setGameState(prev => ({
      ...prev,
      players: prev.players.map(p => {
        if (p.id !== playerId) return p;

        const updatedArmy = { ...p.army };
        updatedArmy.siegeEquipment = updatedArmy.siegeEquipment.map(siege => {
          if (siege.id === siegeId && siege.manning.length < 4 && !siege.manning.includes(soldierId)) {
            return { ...siege, manning: [...siege.manning, soldierId] };
          }
          return siege;
        });

        return { ...p, army: updatedArmy };
      })
    }));
  };

  const unmanSiegeEquipment = (playerId: string, siegeId: string, soldierId: string) => {
    setGameState(prev => ({
      ...prev,
      players: prev.players.map(p => {
        if (p.id !== playerId) return p;

        const updatedArmy = { ...p.army };
        updatedArmy.siegeEquipment = updatedArmy.siegeEquipment.map(siege => {
          if (siege.id === siegeId) {
            return { ...siege, manning: siege.manning.filter(id => id !== soldierId) };
          }
          return siege;
        });

        return { ...p, army: updatedArmy };
      })
    }));
  };

  const getPlayer = (playerId: string) => {
    return gameState.players.find(p => p.id === playerId);
  };

  const validateAndFixArmy = (player: Player, unitStats: Record<SoldierType, UnitStats>): Player => {
    const calculateArmyCost = (army: Army): number => {
      let cost = 0;
      if (army.king) cost += unitStats['King'].cost;
      cost += army.knights.length * unitStats['Knight'].cost;
      cost += army.archers.length * unitStats['Archer'].cost;
      cost += army.crossbows.length * unitStats['Crossbow'].cost;
      cost += army.healers.length * unitStats['Healer'].cost;
      cost += army.siegeEquipment.length * unitStats['Siege Equipment'].cost;
      return cost;
    };

    let updatedArmy = { ...player.army };
    let currentCost = calculateArmyCost(updatedArmy);

    // Remove units in priority order: Knights → Healers → Archers → Crossbows → Siege Equipment
    while (currentCost > player.raidPoints) {
      if (updatedArmy.knights.length > 0) {
        updatedArmy.knights = updatedArmy.knights.slice(0, -1);
      } else if (updatedArmy.healers.length > 0) {
        updatedArmy.healers = updatedArmy.healers.slice(0, -1);
      } else if (updatedArmy.archers.length > 0) {
        updatedArmy.archers = updatedArmy.archers.slice(0, -1);
      } else if (updatedArmy.crossbows.length > 0) {
        updatedArmy.crossbows = updatedArmy.crossbows.slice(0, -1);
      } else if (updatedArmy.siegeEquipment.length > 0) {
        updatedArmy.siegeEquipment = updatedArmy.siegeEquipment.slice(0, -1);
      } else {
        // Can't remove anything else, break to avoid infinite loop
        break;
      }
      currentCost = calculateArmyCost(updatedArmy);
    }

    return { ...player, army: updatedArmy };
  };

  const updateUnitStats = async (soldierType: SoldierType, stats: Partial<UnitStats>) => {
    try {
      setError(null);

      // Update backend first
      await gameService.updateUnitStats(soldierType, stats);

      // Update local state
      setGameState(prev => {
        const newUnitStats = {
          ...prev.unitStats,
          [soldierType]: {
            ...prev.unitStats[soldierType],
            ...stats
          }
        };

        // Validate and fix all player armies
        const validatedPlayers = prev.players.map(player =>
          validateAndFixArmy(player, newUnitStats)
        );

        return {
          ...prev,
          unitStats: newUnitStats,
          players: validatedPlayers
        };
      });

      // Refresh to get validated armies from backend
      await refreshGameState();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update unit stats';
      setError(errorMessage);
      console.error('Failed to update unit stats:', err);
      throw err;
    }
  };

  return (
    <GameContext.Provider value={{
      gameState,
      loading,
      error,
      addPlayer,
      removePlayer,
      updatePlayerRaidPoints,
      switchPlayerTeam,
      addSoldierToArmy,
      removeSoldierFromArmy,
      resetPlayerArmy,
      manSiegeEquipment,
      unmanSiegeEquipment,
      getPlayer,
      updateUnitStats,
      linkUserIdToPlayer,
      getOrCreatePlayerForUser,
      refreshGameState
    }}>
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};
